import { FC } from 'react';
import { Avatar } from '@material-ui/core';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import styled from '@emotion/styled';

import { Project, Variation, RoundOfProject } from '../../../../generated/graphql';
import { SectionTitle, BodySmall as _BodySmall, Link, BodyBoldStyle } from '../../primitive/TypographyUI';
import { PaperContainer } from '../../primitive/Paper';
import Spinner from '../../primitive/Spinner';
import { SPACING_REGULAR } from '../../../constant/spacing';
import VariationStatusDisplay from './VariationStatusDisplay';
import RoundStatusDisplay from './RoundStatusDisplay';
import VariationActions from './VariationActions';
import SubmitCommentDisplay from './SubmitCommentDisplay';
import PreviewActions from './PreviewActions';

type Props = {
  project: Project;
  rounds: RoundOfProject[];
  variation: Variation;
  onTabChange: (_: any, value: string) => void;
  refetch: () => void;
};

const Overview: FC<Props> = ({ project, rounds, variation, onTabChange, refetch }) => {
  const { user } = variation;
  const currentRound = rounds.find(({ id }) => id === variation.latest_round_variation?.v2_round_id);
  const isPausedVariation = currentRound?.variations.find((_variation) => _variation.id === variation.id)!.paused;

  return (
    <ColumnFlexWrapper>
      <PaperContainer>
        {/* @ts-ignore: ts(2322) component prop does accept html tag names */}
        <SectionTitle component="h3">概要</SectionTitle>
        <BodySmall>
          エクスペリエンス: <Link to={`../${project.id}`}>{project.title}</Link>
        </BodySmall>
        <UserInfoSection>
          <AvatarLink to={`../../../growth-hacker/${user.id}`}>
            <Avatar src={user.avatar_image_url} />
            <BodyBold>{user.username}</BodyBold>
          </AvatarLink>
          <div>
            {variation?.state === 'draft' ? '最終更新' : '投稿日'}: {new Date(variation.created_at).toLocaleString()}
          </div>
        </UserInfoSection>
        <VariationStatusDisplay
          project={project}
          variation={variation!}
          isPausedVariation={isPausedVariation}
          onTabChange={onTabChange}
        />
        {variation.state !== 'draft' && (
          <RoundStatusDisplay
            project={project}
            variation={variation!}
            isPausedVariation={isPausedVariation}
            refetch={refetch}
          />
        )}
        <Borderline />
        <VariationActions project={project} variation={variation!} refetch={refetch} />
      </PaperContainer>
      <PaperContainer>
        <RowFlexWrapper>
          {/* @ts-ignore: ts(2322) component prop does accept html tag names */}
          <SectionGrowTitle component="h3">デザイン案の確認</SectionGrowTitle>
          <PreviewActions project={project} variation={variation} refetch={refetch} />
        </RowFlexWrapper>
        <RowFlexNoWrapper>
          <ScreenshotDisplay>
            {variation.permissions?.show_screenshot_image?.permit ? (
              variation.banner_image_url || variation.original_screenshot_image ? (
                <Screenshot
                  src={variation.banner_image_url ? variation.banner_image_url : variation?.original_screenshot_image}
                />
              ) : (
                <ImageOutlinedIcon fontSize="large" />
              )
            ) : (
              <Spinner />
            )}
          </ScreenshotDisplay>
          <SubmitCommentDisplay variation={variation} refetch={refetch} />
        </RowFlexNoWrapper>
      </PaperContainer>
    </ColumnFlexWrapper>
  );
};

const ColumnFlexWrapper = styled('div')(`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 2rem;
  padding-bottom: 4rem;
`);

const UserInfoSection = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const AvatarLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
});

const BodyBold = styled('div')({
  ...BodyBoldStyle,
});

const BodySmall = styled(_BodySmall)({
  margin: `${SPACING_REGULAR}px 0`,
});

const Borderline = styled('div')(`
  height: 1px;
  border: 1px solid #E6E7E8;
  width: 100%;
  margin: 24px 0;
`);

const RowFlexWrapper = styled('div')(`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
`);

const RowFlexNoWrapper = styled(RowFlexWrapper)(`
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 2rem;
`);

const SectionGrowTitle = styled(SectionTitle)(`
  flex: 1 0 auto;
`);

const ScreenshotDisplay = styled('div')(`
  flex: 1 1 60%;
  display: flex;
  padding: 12px 16px;
  background: #F5F6F7;
  box-shadow: 0px 1px 9px rgba(0, 0, 0, 0.04);
  border-radius: 8px;
`);
const Screenshot = styled('img')(`
  padding: 2rem 4rem;
  width: 100%;
  flex: 0 1 auto;
`);

export default Overview;
